// Shared prop tab definitions and matching logic for player-prop categories.
// Used by SingleGame view and AllProps page so the menu options stay in sync.

// Category order per sport follows PRO-127 spec (volume-driven, descending),
// with one exception:
//   MLB is PM-curated under PRO-171 — Home Runs sits between Pitching Outs
//   and Total Bases rather than at the volume-descending tail.
//
//   NBA:   Points -> Rebounds -> P+R+A -> Assists -> 3 Point FG -> DOUBLE+DOUBLE
//   NFL:   Receiving -> Rushing -> Passing -> TDs
//   NCAAF: Rushing -> Receiving -> Passing -> TDs
//   MLB:   Strikeouts -> Pitching Outs -> Home Runs -> Total Bases
//   NHL:   Shots on Goal -> Saves
//
// PRO-171 also adds market-aware visibility: callers that render the tab
// strip should use `getPropTabsWithMarkets(sport, league, games)` so a
// category with zero matching markets in the current game set is hidden
// (e.g. no `Total Bases` tab on a slate that has no Total Bases markets).
// `getPropTabsForSport` still returns the full catalog and remains the
// source for ordering and keyword lookup.
export const propTabKeywords = {
  baseball: {
    'Total Strikeouts': [
      '(TOTAL STRIKEOUTS)',
      'TOTAL STRIKEOUTS',
      'STRIKEOUTS',
    ],
    'Pitching Outs': ['(PITCHING OUTS)', 'PITCHING OUTS'],
    'Home Runs': ['(HOME RUNS)', 'HOME RUNS'],
    'Total Bases': ['(TOTAL BASES)', 'TOTAL BASES'],
  },
  basketball: {
    Points: ['(POINTS)', 'TOTAL POINTS'],
    Rebounds: ['(REBOUNDS)', 'TOTAL REBOUNDS'],
    'P+R+A': [
      '(P+R+A)',
      'PTS & REBS & ASTS',
      'PTS + REB + AST',
      'PTS+REBS+ASTS',
      'P+R+A',
    ],
    Assists: ['(ASSISTS)', 'TOTAL ASSISTS'],
    '3 Point FG': [
      '(3 POINT FG)',
      'TOTAL THREES MADE',
      'TOTAL 3 PT FG',
      '3 PT FG',
      'THREES MADE',
    ],
    'DOUBLE+DOUBLE': ['(DOUBLE+DOUBLE)', 'DOUBLE+DOUBLE', 'DOUBLE DOUBLE'],
  },
  football: {
    'Total Receiving Yards': [
      '(TOTAL RECEIVING YARDS)',
      'TOTAL RECEIVING YARDS',
      'TOTAL RECEIVING YDS',
      'RECEIVING YARDS',
      'RECEIVING YDS',
    ],
    'Total Rushing Yards': [
      '(TOTAL RUSHING YARDS)',
      'TOTAL RUSHING YARDS',
      'TOTAL RUSHING YDS',
      'RUSHING YARDS',
      'RUSHING YDS',
    ],
    'Total Passing Yards': [
      '(TOTAL PASSING YARDS)',
      'TOTAL PASSING YARDS',
      'TOTAL PASSING YDS',
      'PASSING YARDS',
      'PASSING YDS',
    ],
    'Total Touchdown Passes': [
      '(TOTAL TOUCHDOWN PASSES)',
      'TOTAL TOUCHDOWN PASSES',
      'TOUCHDOWN PASSES',
    ],
  },
  // NCAAF flips the football order to lead with rushing (PRO-127 §Per-sport).
  ncaaFootball: {
    'Total Rushing Yards': [
      '(TOTAL RUSHING YARDS)',
      'TOTAL RUSHING YARDS',
      'TOTAL RUSHING YDS',
      'RUSHING YARDS',
      'RUSHING YDS',
    ],
    'Total Receiving Yards': [
      '(TOTAL RECEIVING YARDS)',
      'TOTAL RECEIVING YARDS',
      'TOTAL RECEIVING YDS',
      'RECEIVING YARDS',
      'RECEIVING YDS',
    ],
    'Total Passing Yards': [
      '(TOTAL PASSING YARDS)',
      'TOTAL PASSING YARDS',
      'TOTAL PASSING YDS',
      'PASSING YARDS',
      'PASSING YDS',
    ],
    'Total Touchdown Passes': [
      '(TOTAL TOUCHDOWN PASSES)',
      'TOTAL TOUCHDOWN PASSES',
      'TOUCHDOWN PASSES',
    ],
  },
  hockey: {
    'Shots on Goal': [
      '(SHOTS ON GOAL)',
      'SHOTS ON GOAL',
      'TOTAL SHOTS ON GOAL',
    ],
    Saves: ['(SAVES)', 'TOTAL SAVES', 'SAVES'],
  },
};

// PRO-123: DB `Game.propCategory` tokens per tab, for server-side category
// fetches. The canonical token per tab is PropFilters.vue's apiCategory
// contract ("Values must match `Game.propCategory` in Mongo"), but ingestion
// paths have historically stored wording variants too — the keyword catalog
// above documents them (e.g. `TOTAL RECEIVING YDS`, `3 PT FG`,
// `PTS + REB + AST`), and the `$in` query is exact-match, so every known
// stored spelling must be enumerated here. Case variants are handled
// server-side (expandPropCategoriesForMongoIn); wording variants are not.
// Keyed by the same sport keys as propTabKeywords; ncaaFootball uses the
// football values.
export const propTabApiCategories = {
  baseball: {
    'Total Strikeouts': ['TOTAL STRIKEOUTS', 'STRIKEOUTS'],
    'Pitching Outs': ['PITCHING OUTS'],
    'Home Runs': ['HOME RUNS'],
    'Total Bases': ['TOTAL BASES'],
  },
  basketball: {
    Points: ['POINTS', 'TOTAL POINTS'],
    Rebounds: ['REBOUNDS', 'TOTAL REBOUNDS'],
    'P+R+A': [
      'PTS & REBS & ASTS',
      'PTS + REB + AST',
      'PTS+REBS+ASTS',
      'P+R+A',
    ],
    Assists: ['ASSISTS', 'TOTAL ASSISTS'],
    '3 Point FG': ['THREES MADE', 'TOTAL THREES MADE', 'TOTAL 3 PT FG', '3 PT FG'],
    'DOUBLE+DOUBLE': ['DOUBLE+DOUBLE', 'DOUBLE DOUBLE'],
  },
  football: {
    'Total Receiving Yards': [
      'TOTAL RECEIVING YARDS',
      'TOTAL RECEIVING YDS',
      'RECEIVING YARDS',
      'RECEIVING YDS',
    ],
    'Total Rushing Yards': [
      'TOTAL RUSHING YARDS',
      'TOTAL RUSHING YDS',
      'RUSHING YARDS',
      'RUSHING YDS',
    ],
    'Total Passing Yards': [
      'TOTAL PASSING YARDS',
      'TOTAL PASSING YDS',
      'PASSING YARDS',
      'PASSING YDS',
    ],
    'Total Touchdown Passes': ['TOTAL TOUCHDOWN PASSES', 'TOUCHDOWN PASSES'],
  },
  hockey: {
    'Shots on Goal': ['SHOTS ON GOAL', 'TOTAL SHOTS ON GOAL'],
    Saves: ['SAVES', 'TOTAL SAVES'],
  },
};

export function getApiCategoriesForTab(sport, tabName) {
  const key = sport === 'ncaaFootball' ? 'football' : sport;
  return propTabApiCategories[key]?.[tabName] || [];
}

export function getPropTabsForSport(sport, league) {
  // NCAAF needs a different order than NFL even though both are `football`.
  if (
    sport === 'football' &&
    typeof league === 'string' &&
    league.toUpperCase().includes('NCAA')
  ) {
    return Object.keys(propTabKeywords.ncaaFootball).map((name) => ({ name }));
  }
  const tabs = propTabKeywords[sport];
  if (!tabs) return [];
  return Object.keys(tabs).map((name) => ({ name }));
}

// Returns the catalog filtered to categories that have at least one matching
// game in `games`. Use this for tab-strip rendering so empty categories are
// hidden. Use `getPropTabsForSport` (unfiltered) for ordering reference or
// when you need the full keyword catalog.
export function getPropTabsWithMarkets(sport, league, games) {
  const tabs = getPropTabsForSport(sport, league);
  if (!Array.isArray(games) || games.length === 0) return [];
  return tabs.filter((tab) =>
    games.some((game) =>
      matchesPropTab(game.eventName, sport, tab.name, game.league || league),
    ),
  );
}

export function matchesPropTab(eventName, sport, tabName, league) {
  if (typeof eventName !== 'string') return false;
  const isNcaaf =
    sport === 'football' &&
    typeof league === 'string' &&
    league.toUpperCase().includes('NCAA');
  const sportKey = isNcaaf ? 'ncaaFootball' : sport;
  const keywords = propTabKeywords[sportKey]?.[tabName];
  if (!keywords) return false;
  const upper = eventName.toUpperCase();
  return keywords.some((kw) => upper.includes(kw));
}
